// Cockpit revamp — Funnel tab.
//
// Bars are clickable; each cohort opens an inline panel with the first
// few enrolments. Cohort definitions:
//   enrolled    — every enrolment row (active + exited)
//   in_sequence — exit_status IS NULL (still being dispatched)
//   engaged     — backend doesn't yet expose engagement; placeholder
//   callback    — exit_status = callback_requested
//   qualified   — exit_status = qualified
//   disqualified — exit_status ∈ {opted_out, removed, nurture_routed}
//                  Also surfaces a why-breakdown by exit_status.
//
// Empty pre-launch state is rendered by the parent — the funnel stays
// post-launch only.
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Callout, Pill } from '../../../ui';
import type { CampaignReportPayload } from '../../../services/campaignsAPI';
import type { CampaignEnrolment } from '../CampaignEnrolments';

export type CohortKey =
  | 'enrolled'
  | 'in_sequence'
  | 'engaged'
  | 'callback'
  | 'qualified'
  | 'disqualified';

interface FunnelBar {
  id: CohortKey;
  label: string;
  count: number;
  pct: number;
  /** Tailwind background class for the filled bar. */
  bar: string;
}

interface CampaignFunnelProps {
  report: CampaignReportPayload | null;
  /** First page of enrolments — used for cohort drilldown samples. */
  enrolments: CampaignEnrolment[];
}

const DISQUALIFIED_STATUSES = new Set([
  'opted_out',
  'removed',
  'nurture_routed',
  'campaign_force_ended',
]);

function buildBars(report: CampaignReportPayload | null): FunnelBar[] {
  const total = report?.active.total_enrolments ?? 0;
  const active = report?.active.active_count ?? 0;
  const qualified = report?.active.qualified_count ?? 0;
  const byExit = report?.active.by_exit_status ?? {};
  const callback = byExit['callback_requested'] ?? 0;
  const disqualified = Object.entries(byExit).reduce(
    (sum, [k, v]) => (DISQUALIFIED_STATUSES.has(k) ? sum + v : sum),
    0,
  );
  const pct = (n: number) => (total > 0 ? Math.round((n / total) * 1000) / 10 : 0);
  return [
    { id: 'enrolled', label: 'Enrolled', count: total, pct: total > 0 ? 100 : 0, bar: 'bg-slate-500' },
    { id: 'in_sequence', label: 'In sequence', count: active, pct: pct(active), bar: 'bg-accent' },
    { id: 'engaged', label: 'Engaged (replied)', count: 0, pct: 0, bar: 'bg-accent/70' },
    { id: 'callback', label: 'Callback requested', count: callback, pct: pct(callback), bar: 'bg-info' },
    { id: 'qualified', label: 'Qualified → opp created', count: qualified, pct: pct(qualified), bar: 'bg-ok' },
    { id: 'disqualified', label: 'Disqualified / opted-out', count: disqualified, pct: pct(disqualified), bar: 'bg-danger' },
  ];
}

function rowsForCohort(
  cohort: CohortKey,
  enrolments: CampaignEnrolment[],
): CampaignEnrolment[] {
  switch (cohort) {
    case 'enrolled':
      return enrolments.slice(0, 5);
    case 'in_sequence':
      return enrolments.filter((e) => e.exit_status === null).slice(0, 5);
    case 'callback':
      return enrolments
        .filter((e) => e.exit_status === 'callback_requested')
        .slice(0, 5);
    case 'qualified':
      return enrolments
        .filter((e) => e.exit_status === 'qualified')
        .slice(0, 5);
    case 'disqualified':
      return enrolments
        .filter((e) => e.exit_status !== null && DISQUALIFIED_STATUSES.has(e.exit_status))
        .slice(0, 5);
    case 'engaged':
    default:
      return [];
  }
}

const CampaignFunnel: React.FC<CampaignFunnelProps> = ({
  report,
  enrolments,
}) => {
  const [cohort, setCohort] = useState<CohortKey | null>(null);
  const bars = useMemo(() => buildBars(report), [report]);
  const rows = cohort ? rowsForCohort(cohort, enrolments) : [];
  const selected = bars.find((b) => b.id === cohort) ?? null;

  // Disqualified why-breakdown.
  const disqualifiedBreakdown = useMemo(() => {
    if (!report) return [];
    return Object.entries(report.active.by_exit_status)
      .filter(([k]) => DISQUALIFIED_STATUSES.has(k))
      .sort((a, b) => b[1] - a[1]);
  }, [report]);

  return (
    <div>
      <div className="flex items-end justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-100">
          Click any cohort to drill in
        </h3>
        {report ? (
          <Pill intent="ok">
            recalculated{' '}
            {new Date(report.generated_at).toLocaleTimeString()}
          </Pill>
        ) : null}
      </div>

      <ul className="flex flex-col gap-2" role="list">
        {bars.map((b) => {
          const active = cohort === b.id;
          return (
            <li key={b.id}>
              <button
                type="button"
                onClick={() => setCohort(active ? null : b.id)}
                className={[
                  'w-full text-left p-2 rounded-ds-md transition-colors duration-150',
                  active
                    ? 'bg-white/[0.04] outline outline-1 outline-white/[0.12]'
                    : 'hover:bg-white/[0.02]',
                ].join(' ')}
                aria-pressed={active}
                aria-label={`${b.label} cohort: ${b.count}`}
              >
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-slate-200">{b.label}</span>
                  <span className="font-mono text-slate-400">
                    {b.count.toLocaleString()} · {b.pct.toFixed(1)}%
                  </span>
                </div>
                <div className="h-2 bg-white/[0.04] rounded-full overflow-hidden">
                  <div
                    className={`${b.bar} h-full opacity-80`}
                    style={{ width: `${Math.max(b.pct, 0)}%` }}
                  />
                </div>
              </button>
            </li>
          );
        })}
      </ul>

      {cohort && selected ? (
        <div className="mt-3 px-3 py-3 rounded-ds-md bg-surface-2 border border-white/[0.06]">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500">
              {selected.label} · showing {rows.length} of {selected.count.toLocaleString()}
            </div>
            <Link
              to="?tab=enrolments"
              className="text-xs text-accent hover:text-accent-hover hover:underline"
            >
              Open all →
            </Link>
          </div>
          {rows.length === 0 ? (
            <p className="text-xs text-slate-500 italic">
              No sample rows available for this cohort yet.
            </p>
          ) : (
            <ol className="flex flex-col divide-y divide-white/[0.04]">
              {rows.map((r) => (
                <li
                  key={r.id}
                  className="grid grid-cols-1 md:grid-cols-3 gap-2 py-2 text-xs"
                >
                  <Link
                    to={`/contacts/${r.contact_id}`}
                    className="text-accent hover:text-accent-hover hover:underline"
                  >
                    {r.contact_name || r.contact_email || r.contact_id.slice(0, 8)}
                  </Link>
                  <span className="text-slate-400">
                    joined {new Date(r.joined_at).toLocaleDateString()}
                  </span>
                  <span className="text-slate-300">
                    {r.exit_status
                      ? r.exit_status.replace(/_/g, ' ')
                      : 'in flight'}
                  </span>
                </li>
              ))}
            </ol>
          )}
          {cohort === 'disqualified' && disqualifiedBreakdown.length > 0 ? (
            <Callout
              tone="warn"
              title="Why didn't these convert?"
              className="mt-3"
            >
              <div className="flex gap-3 flex-wrap">
                {disqualifiedBreakdown.map(([k, v]) => (
                  <span key={k} className="text-xs">
                    <strong>{k.replace(/_/g, ' ')}</strong> · {v}
                  </span>
                ))}
              </div>
            </Callout>
          ) : null}
          {cohort === 'engaged' ? (
            <Callout tone="neutral" className="mt-3">
              Engagement signals (reply, callback request mid-step) aren't yet
              broken out by the reporting endpoint. Until then this cohort
              renders empty — see{' '}
              <code className="font-mono">/campaigns/{'{'}id{'}'}/report</code>.
            </Callout>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default CampaignFunnel;
